/**
 * time-exam-gen — 康軒數學三下「時間」單元(課本第 7 章「時間計算」/ 拍照課本目錄
 * ①1日24小時 ②時、分、秒 ③時分秒的加減計算)之**時間概念 + 換算 + 加減**考卷產生器。
 * 對照 `math-exam-gen.ts` / `stats-table-gen.ts` 同一套 lockdown 架構(**seeded RNG +
 * 策展閉集模板 + 受控變數 + 程式可驗證答案 + validator + 200-seed regression**)。
 *
 * grounded 在康軒三下「時間」課本真實內容(boss 拍攝 IMG_2192~2207):
 *   ① daypart 一日24小時概念 —— 某時刻屬凌晨/上午/下午/晚上;半夜12時(0時)→隔天0時 = 24小時;
 *      「1 日 = 24 小時」「中午 12 時」「半夜 12 時 = 0 時」。
 *   ② convert 12時制↔24時制換算(課本重點)—— 下午X時=(X+12)時(下午1時=13時、下午8時30分=20:30)、
 *      上午X時=X時、半夜12時=0時、中午12時=12時。**雙向**(12→24 與 24→12 都出),round-trip 驗。
 *   ③ ampm AM/PM 表示 —— 上午=AM、下午=PM(上午8:30=8:30AM、下午8:30=8:30PM);雙向。
 *   ④ elapsed 時分秒加減 / 經過時間 —— X時Y分 + Z分(可跨時進位,如 11:30 過 2 時 30 分 = 14:00)、
 *      兩時刻相差幾時幾分、X分 = 幾時幾分(60分=1時)。**復用 stats-table-gen 的 formatHM/formatElapsed**
 *      (時間存「午夜起算分鐘數」整數,程式算可驗證)。
 *
 * 鐵則 / 設計(對齊 feedback_generator_source_lockdown,不打地鼠):
 *   - **seeded RNG**(別用 Math.random):同 seed → 同卷,可重現;UI「重抽」= 換 seed。
 *   - **答案程式可驗證**:每題 answer 都由 verify 規格反算(`recomputeTimeAnswer`),validator
 *     重算後比對。12/24 換算用同一條「12 制 ↔ 分鐘數」純函式雙向算,**round-trip 一致**。
 *   - **數值鎖在三下合理範圍**:時刻合法(0-23 時、0-59 分)、跨時進位正確、經過時間非負。
 *   - **lockdown 防壞題**:模板 = 策展閉集常數;硬規則寫進產生器 + validator;200-seed regression 全過。
 *
 * 學習/遊戲分離:純學習側,**絕不**碰 commanderXp / 戰鬥數值。
 */

import { formatHM, formatElapsed } from './stats-table-gen';

// 重新匯出時間工具(供 UI / 測試由本模組取用,單一真相仍在 stats-table-gen)。
export { formatHM, formatElapsed };

// ───────────────────────────── 型別 ─────────────────────────────

/** 題型(四種)。 */
export type TimeQuestionKind = 'daypart' | 'convert' | 'ampm' | 'elapsed';

/** 12 時制時段詞(課本用詞):上午 / 下午 / 中午 / 半夜。 */
export type TwelvePeriod = '上午' | '下午' | '中午' | '半夜';

/**
 * 一題的反算規格(closed set)。validator / 線上批改依 `op` 重算 → 與 `answer` 比對。
 *   - 'daypart':某時刻(24 制分鐘數)屬哪個時段('凌晨'/'上午'/'下午'/'晚上')。
 *   - 'day-hours':一天有幾小時(恆 '24 小時';概念題)。
 *   - 'to24':12 制(period + 時 + 分)→ 24 制 'hh:mm'。
 *   - 'to12':24 制分鐘數 → 12 制中文('下午 8 時 30 分' / '中午 12 時' / '半夜 12 時'…)。
 *   - 'to-ampm':12 制(period + 時 + 分)→ 'h:mm AM' / 'h:mm PM'。
 *   - 'from-ampm':'h:mm AM/PM' → 12 制中文('上午 8 時 30 分'…)。
 *   - 'add':起始(24 制分鐘數)+ 經過分鐘 → 結束 'hh:mm'(可跨整時進位)。
 *   - 'diff':兩時刻(24 制分鐘數)相差 → 經過時間('X 時 Y 分' / 'Y 分鐘',非負)。
 *   - 'min-to-hm':Y 分 → 幾時幾分('X 時 Y 分' / 'Y 分鐘';60 分 = 1 時)。
 */
export type TimeVerify =
  | { op: 'daypart'; minutes: number }
  | { op: 'day-hours' }
  | { op: 'to24'; period: TwelvePeriod; hour12: number; minute: number }
  | { op: 'to12'; minutes: number }
  | { op: 'to-ampm'; period: TwelvePeriod; hour12: number; minute: number }
  | { op: 'from-ampm'; ampm: 'AM' | 'PM'; hour12: number; minute: number }
  | { op: 'add'; start: number; deltaMin: number }
  | { op: 'diff'; a: number; b: number }
  | { op: 'min-to-hm'; minutes: number };

/** 一道時間題:題幹 + 程式可驗證答案 + 反算用 verify。 */
export interface TimeQuestion {
  id: string;
  /** 題幹(顯示 / 列印用)。 */
  prompt: string;
  /** 正解人類可讀字串(顯示 / 批改用)。 */
  answer: string;
  /** 反算規格(validator / 批改重算用)。 */
  verify: TimeVerify;
}

/** 一大題 = 一種題型 + 題目陣列 + 標籤 / 標題 / 配分。 */
export interface TimeSection {
  kind: TimeQuestionKind;
  /** 大題標籤,例 '一'。 */
  label: string;
  /** 大題標題,例 '12 時制 ↔ 24 時制換算'。 */
  title: string;
  /** 作答指示語。 */
  instructions: string;
  /** 每題配分。 */
  pointsEach: number;
  /** 該大題的題目。 */
  questions: TimeQuestion[];
}

/** 一份時間考卷。 */
export interface TimeExam {
  id: string;
  title: string;
  /** 教科書版本(數學 = 康軒)。 */
  textbook: '康軒';
  /** 範圍標註(顯示用)。 */
  scope: string;
  sections: TimeSection[];
}

/** 各題型題數(預設見 DEFAULT_COUNTS;key = TimeQuestionKind)。 */
export type TimeCounts = Partial<Record<TimeQuestionKind, number>>;

/** `generateTimeExam` 選項。 */
export interface TimeGenOpts {
  /** 可重現種子(同 seed 同卷);UI「重抽」= 換 seed。 */
  seed: number;
  /** 各題型題數,預設見 DEFAULT_COUNTS。 */
  counts?: TimeCounts;
  /** 卷 id(預設 `time-<seed>`)。 */
  id?: string;
  /** 卷名(預設 `時間考卷 · 第 <seed> 回`)。 */
  title?: string;
}

// ───────────────────────────── seeded RNG ─────────────────────────────

/**
 * 確定性 RNG —— mulberry32(與 math-exam-gen / stats-table-gen 同款,純函式可重現)。
 * 注入 seed,杜絕 Math.random。
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** [min, max] 的整數(含兩端)。 */
function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** 抽一個元素。 */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

// ───────────────────────────── 時間換算核心(12 制 ↔ 24 制分鐘數)─────────────────────────────

/**
 * 12 制(時段 + 時 + 分)→ 24 制「午夜起算分鐘數」(0..1439)。**換算唯一真相**。
 * 課本規則:
 *   - 上午 X 時 = X 時(X 為 1..11;上午沒有 12,12 是中午)。
 *   - 下午 X 時 = (X + 12) 時(X 為 1..11;下午沒有 12,12 是半夜隔天)。
 *   - 中午 12 時 = 12 時(720 分)。
 *   - 半夜 12 時 = 0 時(0 分)。
 * 規格:hour12 / minute 由 caller 保證合法(見 build12 受控生成)。
 */
function twelveToMinutes(period: TwelvePeriod, hour12: number, minute: number): number {
  let h24: number;
  if (period === '半夜') {
    h24 = 0; // 半夜 12 時 = 0 時。
  } else if (period === '中午') {
    h24 = 12; // 中午 12 時 = 12 時。
  } else if (period === '上午') {
    h24 = hour12; // 上午 1~11 時。
  } else {
    h24 = hour12 + 12; // 下午 1~11 時 → 13~23 時。
  }
  return h24 * 60 + minute;
}

/**
 * 24 制「午夜起算分鐘數」→ 12 制中文字串(報讀 / to12 答案)。**換算唯一真相**(逆向)。
 *   - 0 分 → '半夜 12 時'(+ 分)。
 *   - 720 分(12:00)→ '中午 12 時'(+ 分)。
 *   - 1~11 時 → '上午 X 時'(+ 分)。
 *   - 13~23 時 → '下午 (h-12) 時'(+ 分)。
 * 整時(分 = 0)略「X 分」。
 */
function minutesToTwelveCN(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const minSuffix = m === 0 ? '' : ` ${m} 分`;
  if (h === 0) return `半夜 12 時${minSuffix}`;
  if (h === 12) return `中午 12 時${minSuffix}`;
  if (h < 12) return `上午 ${h} 時${minSuffix}`;
  return `下午 ${h - 12} 時${minSuffix}`;
}

/**
 * 12 制(時段 + 時 + 分)→ AM/PM 字串('h:mm AM' / 'h:mm PM')。**AM/PM 唯一真相**。
 * 上午 / 半夜(凌晨段)→ AM;下午 / 中午 → PM。分補零兩位(8:30 → '8:30 AM')。
 */
function twelveToAmpm(period: TwelvePeriod, hour12: number, minute: number): string {
  // AM = 半夜 / 上午;PM = 中午 / 下午。
  const ampm = period === '上午' || period === '半夜' ? 'AM' : 'PM';
  const displayHour = period === '半夜' || period === '中午' ? 12 : hour12;
  return `${displayHour}:${String(minute).padStart(2, '0')} ${ampm}`;
}

/**
 * 'h:mm AM/PM'(period 由 ampm + hour12 決定)→ 12 制中文字串(from-ampm 答案)。
 * 先轉成分鐘數再用 minutesToTwelveCN(與 to12 共用逆向真相,保持一致)。
 */
function ampmToTwelveCN(ampm: 'AM' | 'PM', hour12: number, minute: number): string {
  // 12 AM = 半夜 0 時;12 PM = 中午 12 時;其餘 AM = 上午、PM = 下午。
  let h24: number;
  if (ampm === 'AM') h24 = hour12 === 12 ? 0 : hour12;
  else h24 = hour12 === 12 ? 12 : hour12 + 12;
  return minutesToTwelveCN(h24 * 60 + minute);
}

/** 某時刻(分鐘數)屬哪個時段詞:凌晨(0-4:59)/ 上午(5-11:59)/ 下午(12-17:59)/ 晚上(18-23:59)。 */
function dayPartOf(minutes: number): string {
  const h = Math.floor(minutes / 60);
  if (h < 5) return '凌晨';
  if (h < 12) return '上午';
  if (h < 18) return '下午';
  return '晚上';
}

// ───────────────────────────── 受控閉集 ─────────────────────────────

/** 分的候選(整十 + 半點,K-3 合理且方便心算;0 加重出現提高整時題比例)。 */
const MINUTE_OPTIONS = [0, 0, 10, 15, 20, 30, 30, 40, 45, 50] as const;

/** 經過時間的「分量」候選(供加法跨整時進位:含 45/75/90/150)。 */
const DELTA_OPTIONS = [20, 30, 40, 45, 60, 75, 90, 120, 150] as const;

/** daypart 概念題的固定情境(時刻 + 時段),grounded 在課本作息圖。 */
interface DaypartScene {
  /** 24 制分鐘數。 */
  minutes: number;
  /** 情境描述(課本作息)。 */
  scene: string;
}

const DAYPART_SCENES: readonly DaypartScene[] = [
  { minutes: 7 * 60, scene: '小明吃早餐' }, // 上午 7:00
  { minutes: 10 * 60, scene: '在學校上第二節課' }, // 上午 10:00
  { minutes: 12 * 60, scene: '中午吃午餐' }, // 下午段邊界(12:00 屬下午)
  { minutes: 15 * 60, scene: '下午在公園玩球' }, // 下午 3:00
  { minutes: 17 * 60, scene: '寫完功課' }, // 下午 5:00
  { minutes: 19 * 60, scene: '吃晚餐' }, // 晚上 7:00
  { minutes: 21 * 60, scene: '準備上床睡覺' }, // 晚上 9:00
  { minutes: 4 * 60, scene: '天還沒亮' }, // 凌晨 4:00
];

// ───────────────────────────── 各題型生成(從規格反算 answer)─────────────────────────────

/**
 * ① daypart:某時刻屬哪個時段(凌晨/上午/下午/晚上)+ 一天有幾小時概念。
 * variant 0/1/2 = 時段判讀(情境);每 4 題穿插一題「一天有幾小時」概念題。
 */
function genDaypartQuestions(rng: () => number, n: number): TimeQuestion[] {
  const out: TimeQuestion[] = [];
  for (let i = 0; i < n; i++) {
    if (i % 4 === 3) {
      // 概念題:一天有幾小時(1 日 = 24 小時)。
      out.push({
        id: `d${i + 1}`,
        prompt: '從半夜 12 時(0 時)到隔天的半夜 12 時(0 時),一天一共有幾小時?',
        answer: '24 小時',
        verify: { op: 'day-hours' },
      });
    } else {
      // 時段判讀:某情境發生在某時刻,屬哪個時段。
      const sc = pick(rng, DAYPART_SCENES);
      out.push({
        id: `d${i + 1}`,
        prompt: `${sc.scene}的時間是 ${formatHM(sc.minutes)}(24 時制),這是一天中的哪個時段?(凌晨 / 上午 / 下午 / 晚上)`,
        answer: dayPartOf(sc.minutes),
        verify: { op: 'daypart', minutes: sc.minutes },
      });
    }
  }
  return out;
}

/** 生一個受控的 12 制時刻(回 period / hour12 / minute,保證換算合法)。 */
function build12(rng: () => number): { period: TwelvePeriod; hour12: number; minute: number } {
  // 80% 上午/下午(1~11 時),20% 中午/半夜(12 時)。
  const r = rng();
  if (r < 0.4) {
    return { period: '上午', hour12: randInt(rng, 1, 11), minute: pick(rng, MINUTE_OPTIONS) };
  }
  if (r < 0.8) {
    return { period: '下午', hour12: randInt(rng, 1, 11), minute: pick(rng, MINUTE_OPTIONS) };
  }
  if (r < 0.9) {
    return { period: '中午', hour12: 12, minute: pick(rng, MINUTE_OPTIONS) };
  }
  return { period: '半夜', hour12: 12, minute: pick(rng, MINUTE_OPTIONS) };
}

/**
 * ② convert:12 時制 ↔ 24 時制換算(雙向)。
 * 偶數題 = 12→24('hh:mm');奇數題 = 24→12(中文)。round-trip 一致由換算真相保證。
 */
function genConvertQuestions(rng: () => number, n: number): TimeQuestion[] {
  const out: TimeQuestion[] = [];
  for (let i = 0; i < n; i++) {
    if (i % 2 === 0) {
      // 12 → 24。
      const t = build12(rng);
      const minutes = twelveToMinutes(t.period, t.hour12, t.minute);
      const minPart = t.minute === 0 ? '' : ` ${t.minute} 分`;
      const hourLabel = t.period === '中午' || t.period === '半夜' ? '12' : String(t.hour12);
      out.push({
        id: `c${i + 1}`,
        prompt: `「${t.period} ${hourLabel} 時${minPart}」用 24 時制怎麼表示?(寫成 hh:mm)`,
        answer: formatHM(minutes),
        verify: { op: 'to24', period: t.period, hour12: t.hour12, minute: t.minute },
      });
    } else {
      // 24 → 12。挑一個有代表性的分鐘數(含中午 / 半夜邊界)。
      const minutes = pick24(rng);
      out.push({
        id: `c${i + 1}`,
        prompt: `「${formatHM(minutes)}」(24 時制)用 12 時制怎麼說?(例:下午 8 時 30 分)`,
        answer: minutesToTwelveCN(minutes),
        verify: { op: 'to12', minutes },
      });
    }
  }
  return out;
}

/** 受控挑一個 24 制分鐘數(覆蓋上午 / 下午 / 中午 / 半夜邊界)。 */
function pick24(rng: () => number): number {
  const r = rng();
  if (r < 0.1) return 0; // 半夜 12 時 = 0 時。
  if (r < 0.2) return 12 * 60 + pick(rng, MINUTE_OPTIONS); // 中午。
  const h = r < 0.6 ? randInt(rng, 1, 11) : randInt(rng, 13, 23);
  return h * 60 + pick(rng, MINUTE_OPTIONS);
}

/**
 * ③ ampm:AM/PM 表示(雙向)。
 * 偶數題 = 12 制中文 → AM/PM;奇數題 = AM/PM → 12 制中文。
 */
function genAmpmQuestions(rng: () => number, n: number): TimeQuestion[] {
  const out: TimeQuestion[] = [];
  for (let i = 0; i < n; i++) {
    if (i % 2 === 0) {
      // 12 制中文 → AM/PM。只用上午 / 下午(AM/PM 最直觀;中午 / 半夜的 12 易混,留給 convert)。
      const period: TwelvePeriod = rng() < 0.5 ? '上午' : '下午';
      const hour12 = randInt(rng, 1, 11);
      const minute = pick(rng, MINUTE_OPTIONS);
      const minPart = minute === 0 ? '' : ` ${minute} 分`;
      out.push({
        id: `a${i + 1}`,
        prompt: `「${period} ${hour12} 時${minPart}」用 AM / PM 怎麼表示?(例:上午 8 時 30 分 = 8:30 AM)`,
        answer: twelveToAmpm(period, hour12, minute),
        verify: { op: 'to-ampm', period, hour12, minute },
      });
    } else {
      // AM/PM → 12 制中文。
      const ampm: 'AM' | 'PM' = rng() < 0.5 ? 'AM' : 'PM';
      const hour12 = randInt(rng, 1, 11);
      const minute = pick(rng, MINUTE_OPTIONS);
      out.push({
        id: `a${i + 1}`,
        prompt: `「${hour12}:${String(minute).padStart(2, '0')} ${ampm}」用 12 時制怎麼說?(例:8:30 PM = 下午 8 時 30 分)`,
        answer: ampmToTwelveCN(ampm, hour12, minute),
        verify: { op: 'from-ampm', ampm, hour12, minute },
      });
    }
  }
  return out;
}

/**
 * ④ elapsed:時分秒加減 / 經過時間。
 * variant 0 = 經過時間加法(起始 + 經過 → 結束,可跨整時進位);
 * variant 1 = 兩時刻相差(diff,非負);
 * variant 2 = 分 → 幾時幾分(60 分 = 1 時,min-to-hm)。
 */
function genElapsedQuestions(rng: () => number, n: number): TimeQuestion[] {
  const out: TimeQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const variant = i % 3;
    if (variant === 0) {
      // 加法:起始時刻 + 經過分鐘 → 結束時刻(可跨整時進位)。
      const start = startTime(rng);
      const delta = pick(rng, DELTA_OPTIONS);
      const end = start + delta;
      out.push({
        id: `e${i + 1}`,
        prompt: `現在是 ${formatHM(start)},經過 ${formatElapsed(delta)},是幾點幾分?(寫成 hh:mm)`,
        answer: formatHM(end),
        verify: { op: 'add', start, deltaMin: delta },
      });
    } else if (variant === 1) {
      // 相差:兩時刻相差幾時幾分(非負,大減小)。
      const t1 = startTime(rng);
      const t2 = t1 + pick(rng, DELTA_OPTIONS);
      out.push({
        id: `e${i + 1}`,
        prompt: `從 ${formatHM(t1)} 到 ${formatHM(t2)},一共經過了多少時間?`,
        answer: formatElapsed(t2 - t1),
        verify: { op: 'diff', a: t1, b: t2 },
      });
    } else {
      // 分 → 幾時幾分(60 分 = 1 時)。
      const minutes = pick(rng, DELTA_OPTIONS);
      out.push({
        id: `e${i + 1}`,
        prompt: `${minutes} 分鐘等於幾時幾分?(60 分 = 1 時;不滿 60 分就寫「Y 分鐘」)`,
        answer: formatElapsed(minutes),
        verify: { op: 'min-to-hm', minutes },
      });
    }
  }
  return out;
}

/**
 * 加法 / 相差題的起始時刻:鎖在 06:00~20:00 整十分,確保 + 最大 delta(150 分)後 < 24:00,
 * 跨整時進位仍合法(end < 1440)。
 */
function startTime(rng: () => number): number {
  const maxStart = 24 * 60 - 150 - 10; // 留 150 分 delta 餘裕。
  let cur = randInt(rng, 6 * 60, Math.min(20 * 60, maxStart));
  cur -= cur % 10;
  return cur + pick(rng, [0, 0, 30]); // 偶爾帶半點,練跨整時。
}

// ───────────────────────────── 組卷 ─────────────────────────────

/**
 * 各題型題數(預設)。四大題(grounded 在課本三大題型),Σ(pointsEach×count) = 84:
 *   daypart 4×4=16(時段判讀 + 一天24小時)/ convert 6×4=24(12↔24 雙向,課本重點)/
 *   ampm 5×4=20(AM/PM 雙向)/ elapsed 6×4=24 → 84。此為「時間」專卷
 *  (題量充足、涵蓋課本三型 + 各型多題種)。
 */
const DEFAULT_COUNTS: Required<TimeCounts> = {
  daypart: 4,
  convert: 6,
  ampm: 5,
  elapsed: 6,
};

/** 固定出題順序(由淺入深):時段概念 → 12↔24 換算 → AM/PM → 時分秒加減。 */
const SECTION_ORDER: TimeQuestionKind[] = ['daypart', 'convert', 'ampm', 'elapsed'];

const SECTION_META: Record<
  TimeQuestionKind,
  { title: string; instructions: string; pointsEach: number }
> = {
  daypart: {
    title: '一天 24 小時(時段判讀)',
    instructions:
      '一天從半夜 12 時(0 時)到隔天的半夜 12 時(0 時),一共 24 小時。讀出時刻屬於哪個時段。每題 4 分。',
    pointsEach: 4,
  },
  convert: {
    title: '12 時制 ↔ 24 時制換算',
    instructions:
      '下午 X 時 = (X + 12) 時(下午 1 時 = 13 時)。上午 X 時 = X 時;半夜 12 時 = 0 時;中午 12 時 = 12 時。每題 4 分。',
    pointsEach: 4,
  },
  ampm: {
    title: 'AM / PM 表示',
    instructions: '上午用 AM、下午用 PM(上午 8 時 30 分 = 8:30 AM、下午 8 時 30 分 = 8:30 PM)。每題 4 分。',
    pointsEach: 4,
  },
  elapsed: {
    title: '時分秒的加減與經過時間',
    instructions:
      '算經過時間要注意跨過整點的進位(60 分 = 1 時)。經過時間不會是負的。每題 4 分。',
    pointsEach: 4,
  },
};

const CN_SECTION_LABELS = ['一', '二', '三', '四', '五', '六'];

/**
 * 從受控閉集生一份時間考卷。同 seed → 同卷(可重現)。
 *
 * @param opts seed(必填)+ 可選題數 / id / title。
 * @returns 一份 `TimeExam`(預設四大題:時段 / 12↔24 換算 / AM-PM / 時分秒加減)。
 *   生成後建議過 `validateTimeExam`。
 */
export function generateTimeExam(opts: TimeGenOpts): TimeExam {
  const rng = makeRng(opts.seed);
  const counts: Required<TimeCounts> = { ...DEFAULT_COUNTS, ...opts.counts };

  const sections: TimeSection[] = SECTION_ORDER.map((kind, idx) => {
    const meta = SECTION_META[kind];
    const n = counts[kind];
    let questions: TimeQuestion[];
    if (kind === 'daypart') {
      questions = genDaypartQuestions(rng, n);
    } else if (kind === 'convert') {
      questions = genConvertQuestions(rng, n);
    } else if (kind === 'ampm') {
      questions = genAmpmQuestions(rng, n);
    } else {
      questions = genElapsedQuestions(rng, n);
    }
    return {
      kind,
      label: CN_SECTION_LABELS[idx] ?? String(idx + 1),
      title: meta.title,
      instructions: meta.instructions,
      pointsEach: meta.pointsEach,
      questions,
    };
  });

  return {
    id: opts.id ?? `time-${opts.seed}`,
    title: opts.title ?? `時間考卷 · 第 ${opts.seed} 回`,
    textbook: '康軒',
    scope: '三下 · 時間(一日 24 小時 / 12↔24 換算 / 時分秒加減)',
    sections,
  };
}

/** 一份考卷的總分(Σ pointsEach×題數)。 */
export function examTotalPoints(exam: TimeExam): number {
  return exam.sections.reduce((sum, s) => sum + s.pointsEach * s.questions.length, 0);
}

// ───────────────────────────── 反算(共用:validator + 計分共用)─────────────────────────────

/**
 * 依一題的 `verify` 規格,重算期望答案字串。validator 與線上批改共用此函式(單一真相)。
 * 規格不合法(時刻越界…)回 null(視為壞題)。
 */
export function recomputeTimeAnswer(v: TimeVerify): string | null {
  switch (v.op) {
    case 'daypart': {
      if (!Number.isInteger(v.minutes) || v.minutes < 0 || v.minutes >= 24 * 60) return null;
      return dayPartOf(v.minutes);
    }
    case 'day-hours':
      return '24 小時';
    case 'to24': {
      if (!isValid12(v.period, v.hour12, v.minute)) return null;
      return formatHM(twelveToMinutes(v.period, v.hour12, v.minute));
    }
    case 'to12': {
      if (!Number.isInteger(v.minutes) || v.minutes < 0 || v.minutes >= 24 * 60) return null;
      return minutesToTwelveCN(v.minutes);
    }
    case 'to-ampm': {
      if (!isValid12(v.period, v.hour12, v.minute)) return null;
      return twelveToAmpm(v.period, v.hour12, v.minute);
    }
    case 'from-ampm': {
      if (
        (v.ampm !== 'AM' && v.ampm !== 'PM') ||
        !Number.isInteger(v.hour12) ||
        v.hour12 < 1 ||
        v.hour12 > 12 ||
        !Number.isInteger(v.minute) ||
        v.minute < 0 ||
        v.minute > 59
      ) {
        return null;
      }
      return ampmToTwelveCN(v.ampm, v.hour12, v.minute);
    }
    case 'add': {
      if (
        !Number.isInteger(v.start) ||
        v.start < 0 ||
        v.deltaMin < 0 ||
        v.start + v.deltaMin >= 24 * 60
      ) {
        return null;
      }
      return formatHM(v.start + v.deltaMin);
    }
    case 'diff': {
      if (!Number.isInteger(v.a) || !Number.isInteger(v.b) || v.a < 0 || v.b < 0) return null;
      if (v.a >= 24 * 60 || v.b >= 24 * 60) return null;
      const diff = Math.abs(v.b - v.a);
      return formatElapsed(diff);
    }
    case 'min-to-hm': {
      if (!Number.isInteger(v.minutes) || v.minutes < 0) return null;
      return formatElapsed(v.minutes);
    }
  }
}

/** 12 制(時段 + 時 + 分)是否合法(供 validator)。 */
function isValid12(period: TwelvePeriod, hour12: number, minute: number): boolean {
  if (!Number.isInteger(hour12) || !Number.isInteger(minute)) return false;
  if (minute < 0 || minute > 59) return false;
  if (period === '上午' || period === '下午') return hour12 >= 1 && hour12 <= 11;
  // 中午 / 半夜恆為 12 時。
  return hour12 === 12;
}

// ───────────────────────────── 驗證器 ─────────────────────────────

/**
 * 驗證一份(產生)時間考卷是否合法。回**錯誤訊息清單**,空陣列 = 合法。
 *
 * 檢查(lockdown 不打地鼠):
 *   - id 唯一、prompt / answer 非空、每大題至少一題。
 *   - **每題 answer == recomputeTimeAnswer(verify)**(反算一致)。
 *   - 12/24 換算 round-trip 一致(分鐘數 → 12 制中文 → 解析回分鐘數)。
 *   - 時刻合法(0-23 時、0-59 分)、經過時間非負、加法不跨日(end < 24:00)。
 */
export function validateTimeExam(exam: TimeExam): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  if (exam.sections.length === 0) errors.push('考卷沒有任何大題');

  for (const section of exam.sections) {
    if (section.questions.length === 0) {
      errors.push(`${section.label}、${section.title}:沒有任何題目`);
    }
    for (const q of section.questions) {
      if (seenIds.has(q.id)) errors.push(`${q.id}: 重複的題 id`);
      seenIds.add(q.id);
      if (!q.prompt || q.prompt.trim() === '') {
        errors.push(`${q.id}: prompt 為空`);
        continue;
      }
      if (!q.answer || q.answer.trim() === '') {
        errors.push(`${q.id}: answer 為空`);
        continue;
      }
      // 加法不跨日硬規則(先於 recompute null 檢查,給出明確「跨日」訊息)。
      if (q.verify.op === 'add' && q.verify.start + q.verify.deltaMin >= 24 * 60) {
        errors.push(`${q.id}: 加法結果跨日(≥ 24:00)`);
        continue;
      }
      const expected = recomputeTimeAnswer(q.verify);
      if (expected === null) {
        errors.push(`${q.id}: verify 規格不合法(${q.verify.op})`);
        continue;
      }
      if (expected !== q.answer) {
        errors.push(`${q.id}: 反算「${expected}」≠ answer「${q.answer}」(${q.verify.op})`);
      }
      // 12/24 換算 round-trip 硬規則:to24 結果對應分鐘數 round-trip 一致。
      if (q.verify.op === 'to24') {
        const minutes = twelveToMinutes(q.verify.period, q.verify.hour12, q.verify.minute);
        if (q.answer !== formatHM(minutes)) {
          errors.push(`${q.id}: 12→24 換算與分鐘數不一致`);
        }
        if (!roundTrip12(minutes)) {
          errors.push(`${q.id}: 12↔24 round-trip 不一致(${minutes})`);
        }
      }
      if (q.verify.op === 'to12') {
        if (!roundTrip12(q.verify.minutes)) {
          errors.push(`${q.id}: 24→12 round-trip 不一致(${q.verify.minutes})`);
        }
      }
    }
  }

  return errors;
}

/**
 * round-trip 一致檢查:分鐘數 → 12 制中文 → 解析回分鐘數,應與原值相同。
 * 證明 minutesToTwelveCN / 解析互逆(12↔24 換算閉環無歧義)。
 */
function roundTrip12(minutes: number): boolean {
  const cn = minutesToTwelveCN(minutes);
  const parsed = parseTwelveCN(cn);
  return parsed === minutes;
}

/** 把 minutesToTwelveCN 的中文字串解析回分鐘數(round-trip 驗證用)。null = 解析失敗。 */
function parseTwelveCN(cn: string): number | null {
  // 形如:'半夜 12 時'、'中午 12 時 30 分'、'上午 7 時'、'下午 3 時 15 分'。
  const m = cn.match(/^(半夜|中午|上午|下午) (\d+) 時(?: (\d+) 分)?$/u);
  if (!m) return null;
  const period = m[1] as TwelvePeriod;
  const hour12 = Number(m[2]);
  const minute = m[3] === undefined ? 0 : Number(m[3]);
  return twelveToMinutes(period, hour12, minute);
}
